/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 글자색 마크 툴을 정의한다.
 *
 * @file /src/rootin/tools/color/Color.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 7.
 */
import { Tool } from '@rootin/base/Tool';
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Language, LanguageText } from '@utils/Language';
import { Menu, type MenuItems } from '@rootin/core/Menu';
import { Dom } from '@utils/Dom';
import './Color.css';

export class Color extends Tool {
    /**
     * 툴타입
     */
    static type: string = 'color';

    /**
     * 툴그룹
     */
    static group: string = 'mark';

    /**
     * 툴아이콘
     */
    icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-palette-icon lucide-palette"><path d="M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"/><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/></svg>';

    /**
     * 툴팁
     */
    text: LanguageText = Language.get('rootin.tools.color');

    /**
     * 적용할 태그.
     */
    #tag = 'span';

    /**
     * 색상을 지정하지 않았을 때 사용할 기본값
     */
    static defaultColors: { [key: string]: string } = {
        'default': 'inherit',
        '--rootin-color-1': 'inherit',
        '--rootin-color-2': 'inherit',
        '--rootin-color-3': 'inherit',
        '--rootin-color-4': 'inherit',
        '--rootin-color-5': 'inherit',
    };

    /**
     * 툴을 렌더링한다.
     */
    async render(): Promise<void> {
        await super.render();

        const colors: { [key: string]: string } = this.editor.getConfig(this, 'colors') ?? Color.defaultColors;
        this.#applyContainerColorVars(colors);
    }

    /**
     * 색상 선택 메뉴를 가져온다. 열 때마다 다시 만들어서 체크표시가 항상 최신 선택상태를
     * 반영하게 한다(캐시해두면 다른 색으로 바꾼 뒤 다시 열었을 때 체크표시가 낡은 채로 남는다).
     *
     * @returns 메뉴
     */
    getMenu(): Menu {
        const colors: { [key: string]: string } = this.editor.getConfig(this, 'colors') ?? Color.defaultColors;

        const menus: MenuItems = [];
        for (const [name, value] of Object.entries(colors)) {
            const isDefault = name === 'default';

            const color = isDefault ? 'currentColor' : name.startsWith('--') ? `var(${name})` : value;
            const selector = isDefault ? `${this.#tag}[style^="color:"]` : `${this.#tag}[style="color: ${color};"]`;
            const isActive = isDefault
                ? this.editor.mark.isActive(selector) === false
                : this.editor.mark.isActive(selector);

            // 기본값(default) 항목은 "색상 마크 해제"를 뜻하므로, 대각선 취소선을 그려 구분한다.
            menus.push({
                text: Color.swatchHtml(color, isActive, isDefault),
                handler: async () => {
                    if (isDefault === true) {
                        this.#clear();
                    } else {
                        this.#toggle(color);
                    }
                    return true;
                },
            });
        }

        const menu = new Menu({ $target: this.$getButton(), menus, removeOnHide: true });
        menu.$getComponent().addClass('rootin-color-menu');
        this.#applyMenuColorVars(menu.$getComponent(), colors);

        menu.$getComponent().on('mousedown', (_, e) => {
            e.preventDefault();
        });

        return menu;
    }

    /**
     * 색상 스와치(그리드 메뉴 항목) HTML을 만든다. `Balloon.ts`의 프로필 색상 메뉴에서도
     * 그대로 재사용한다.
     *
     * @param color - 스와치에 채울 색상(`var(--x)` 또는 리터럴 CSS 색상값)
     * @param active - 현재 선택된 색상인지 여부(체크 표시)
     * @param canceled - 대각선 취소선을 그릴지 여부(기본값=색상 마크 해제를 뜻하는 스와치용, 실제
     *   색상으로 쓰이는 스와치에는 넣지 않는다)
     * @returns 스와치 HTML 문자열
     */
    static swatchHtml(color: string, active: boolean, canceled: boolean = false): string {
        const check = active
            ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 0 1.5px rgba(0,0,0,.7));"><path d="M20 6 9 17l-5-5"/></svg>'
            : '';
        const cancel = canceled ? '<i data-role="cancel"></i>' : '';

        return `<i class="rootin-color-swatch" style="background:${color};">${check}${cancel}</i>`;
    }

    /**
     * 에디터에 컬러 툴이 설정되어 있는 경우, 색상목록의 실제색상코드를 가져온다.
     *
     * @param editor - 대상 에디터
     * @param colors - 색상 설정(미지정시 에디터에 실제 설정된 값, 그것도 없으면 기본값)
     * @returns CSS 변수명 -> 리터럴 색상값 맵(컬러 툴이 없으면 빈 맵)
     */
    static resolvePalette(editor: Editor, colors?: { [key: string]: string }): Map<string, string> {
        const map = new Map<string, string>();

        const colorTool = editor.tools.find((tool) => tool.getType() === 'color');
        if (colorTool === undefined) {
            return map;
        }

        colors ??= editor.getConfig(colorTool, 'colors') ?? Color.defaultColors;
        const container = editor.$getContainer().getEl();
        for (const name of Object.keys(colors)) {
            if (name === 'default' || name.startsWith('--') === false) {
                continue;
            }

            const resolved = getComputedStyle(container).getPropertyValue(name).trim();
            if (resolved !== '') {
                map.set(name, resolved);
            }
        }

        return map;
    }

    /**
     * HTML의 각 DOM 의 스타일을 색상변수로 변경한다.
     *
     * @param editor - 팔레트 설정을 가져올 에디터
     * @param html - HTML 문자열
     * @returns HTML 문자열
     */
    static sanitizeStyles(editor: Editor, html: string): string {
        const palette = Color.resolvePalette(editor);
        const paletteValues = [...palette.values()];

        // 리터럴 값(정규화된 형태) -> 변수명 으로 뒤집는다.
        const byColor = new Map<string, string>();
        for (const [name, value] of palette) {
            const normalized = Color.#normalizeColor(value);
            if (normalized !== null) {
                byColor.set(normalized, name);
            }
        }

        const $wrap = document.createElement('div');
        $wrap.innerHTML = html;

        const walk = (el: globalThis.Element): void => {
            for (const child of Array.from(el.children)) {
                walk(child);

                const target = child as HTMLElement;
                if (target.hasAttribute('style') === false) {
                    continue;
                }

                const kept: string[] = [];
                for (const prop of ['color', 'background-color']) {
                    const value = target.style.getPropertyValue(prop);
                    if (value === '') {
                        continue;
                    }

                    const varMatch = value.match(/^var\((--[\w-]+)\)$/);
                    if (varMatch !== null && palette.has(varMatch[1]) === true) {
                        kept.push(`${prop}: ${value};`);
                        continue;
                    }

                    const matched = paletteValues.length > 0 ? Color.matchColor(value, paletteValues) : null;
                    if (matched === null) {
                        continue;
                    }

                    const normalized = Color.#normalizeColor(matched);
                    const name = normalized !== null ? byColor.get(normalized) : undefined;
                    kept.push(`${prop}: ${name !== undefined ? `var(${name})` : matched};`);
                }

                if (kept.length > 0) {
                    target.setAttribute('style', kept.join(' '));
                } else {
                    target.removeAttribute('style');
                }
            }
        };
        walk($wrap);

        return $wrap.innerHTML;
    }

    /**
     * 툴 클릭시 실행할 핸들러를 정의한다.
     */
    handler(): void {
        this.getMenu().show(false);
    }

    /**
     * 툴의 표시여부 조건을 설정한다.
     *
     * @param block - 커서가 위치한 블록
     * @param element - 커서가 위치한 편집유닛
     * @returns 표시여부
     */
    visible(block: Block | null, element: Element | null): boolean {
        return true;
    }

    /**
     * 툴이 활성여부 조건을 설정한다.
     *
     * @returns 활성여부
     */
    active(): boolean {
        return this.editor.mark.isActive(`${this.#tag}[style^="color:"]`);
    }

    /**
     * 임의의 CSS 색상 문자열을 표기와 무관하게 비교할 수 있도록 정규화한다.
     *
     * @param value - CSS 색상 문자열
     * @returns 정규화된 색상 문자열(해석 불가하면 null)
     */
    static #normalizeColor(value: string): string | null {
        const probe = document.createElement('div');
        probe.style.color = '';
        probe.style.color = value;
        if (probe.style.color === '') {
            return null;
        }

        document.body.appendChild(probe);
        const resolved = getComputedStyle(probe).color;
        probe.remove();
        return resolved;
    }

    /**
     * 색상값을 RGB로 정규화하기 위한 캐시
     */
    static #rgbCache: Map<string, [number, number, number] | null> = new Map();

    /**
     * 임의의 CSS 색상 문자열을 RGB 채널값으로 정규화한다.
     *
     * @param value - CSS 색상 문자열
     * @returns [r, g, b] (해석 불가한 값이면 null)
     */
    static #toRgb(value: string): [number, number, number] | null {
        if (Color.#rgbCache.has(value)) {
            return Color.#rgbCache.get(value)!;
        }

        const probe = document.createElement('div');
        probe.style.color = '';
        probe.style.color = value;
        if (probe.style.color === '') {
            Color.#rgbCache.set(value, null);
            return null;
        }

        document.body.appendChild(probe);
        const resolved = getComputedStyle(probe).color;
        probe.remove();

        const matched = resolved.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
        const rgb: [number, number, number] | null =
            matched === null ? null : [parseFloat(matched[1]), parseFloat(matched[2]), parseFloat(matched[3])];

        Color.#rgbCache.set(value, rgb);
        return rgb;
    }

    /**
     * 두 RGB 색상의 지각적 거리를 근사한다.
     *
     * @param a - 색상 RGB
     * @param b - 색상 RGB
     * @returns 거리(작을수록 비슷함, 0이면 동일)
     */
    static #colorDistance(a: [number, number, number], b: [number, number, number]): number {
        const rMean = (a[0] + b[0]) / 2;
        const dr = a[0] - b[0];
        const dg = a[1] - b[1];
        const db = a[2] - b[2];
        return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db);
    }

    /**
     * 색상값을 허용목록과 대조해, 비슷한 색상이 존재하면 해당 색상으로 매칭한다.
     *
     * @param value - 판정할 색상값
     * @param allowed - 허용 색상 목록
     * @param threshold - 이 거리 이하일 때만 매칭으로 인정
     * @returns 매칭된 허용목록의 색상값(허용목록에 있는 원래 표기 그대로) — 매칭 실패시 null
     */
    static matchColor(value: string, allowed: string[], threshold: number = 40): string | null {
        const rgb = Color.#toRgb(value);
        if (rgb === null) {
            return null;
        }

        let best: { color: string; distance: number } | null = null;
        for (const candidate of allowed) {
            const candidateRgb = Color.#toRgb(candidate);
            if (candidateRgb === null) {
                continue;
            }

            const distance = Color.#colorDistance(rgb, candidateRgb);
            if (best === null || distance < best.distance) {
                best = { color: candidate, distance };
            }
        }

        return best !== null && best.distance <= threshold ? best.color : null;
    }

    /**
     * 선택영역(또는 캐럿)에 지정한 색상 마크를 토글한다.
     *
     * @param color - 적용할 색상(`var(--rootin-color-1)` 또는 `#000000` 같은 CSS 색상값)
     */
    #toggle(color: string): void {
        const style = `color: ${color};`;
        this.editor.mark.toggle(this.#tag, `${this.#tag}[style="${style}"]`, { style });
    }

    /**
     * 선택영역의 색상 마크를 지금 무슨 색이든 상관없이 전부 벗겨낸다(`default` 항목용).
     */
    #clear(): void {
        this.editor.mark.clear(`${this.#tag}[style^="color:"]`);
    }

    /**
     * 색상설정에 따라 메뉴에 추가해준다.
     *
     * @param $menu - 메뉴 컴포넌트 DOM
     * @param colors - 색상 설정
     */
    #applyMenuColorVars($menu: Dom, colors: { [key: string]: string }): void {
        for (const [name, resolved] of Color.resolvePalette(this.editor, colors)) {
            $menu.setStyle(name, resolved);
        }
    }

    /**
     * 색상변수를 에디터 컨테이너 등록한다.
     *
     * @param colors - 색상 설정
     */
    #applyContainerColorVars(colors: { [key: string]: string }): void {
        const $container = this.editor.$getContainer();
        for (const [name, value] of Object.entries(colors)) {
            if (name === 'default' || name.startsWith('--') === false || value === 'inherit') {
                continue;
            }

            $container.setStyle(name, value);
        }
    }
}
